import { z } from 'zod';

/**
 * Schema and types for the order-status read-only lookup response.
 *
 * Design: minimal, graceful, no over-modelling — A-Manager `individual-maket`
 * contract is unknown until the commerce-transactional-core epic wires it.
 * Not-found shape = { found: false, status: null, orderRef: null, placedAt: null, updatedAt: null }.
 *
 * Source: 2026-06-20-wandero-order-status-lookup-page.
 */

/** Closed enum of displayable order lifecycle states. */
export const OrderStatusEnum = z.enum([
  'pending',
  'processing',
  'in_production',
  'shipped',
  'delivered',
  'cancelled',
]);
export type OrderStatusValue = z.infer<typeof OrderStatusEnum>;

/** Full response from GET /v1/orders/:token/status. */
export const OrderStatusResponseSchema = z.object({
  /** true when the token matched a known order. */
  found: z.boolean(),
  /** Order lifecycle state, or null when found is false. */
  status: OrderStatusEnum.nullable(),
  /** Human-readable order reference (NOT the token), or null. */
  orderRef: z.string().nullable(),
  /** ISO-8601 string when the order was placed, or null. */
  placedAt: z.string().nullable(),
  /** ISO-8601 string of the last status update, or null. */
  updatedAt: z.string().nullable(),
});
export type OrderStatusResponse = z.infer<typeof OrderStatusResponseSchema>;

/** Canonical not-found shape — reused by api route and client data fn. */
export const ORDER_STATUS_NOT_FOUND: OrderStatusResponse = {
  found: false,
  status: null,
  orderRef: null,
  placedAt: null,
  updatedAt: null,
};

/**
 * Affinity recommendations for the confirmation-screen cross-sell rail.
 *
 * GET /v1/orders/:token/recommendations?lang=&limit=
 *
 * Ranking (see api/src/queries/catalog.ts getAffinityRecommendations):
 *   1. Wallpapers sharing a category with the purchased wallpaper(s),
 *      excluding the purchased wallpaper(s) themselves, ranked by
 *      wallpaper_order_stats.orders_count DESC (wallpaper_id ASC tie-break).
 *   2. Padded with featured wallpapers when the affinity set is thin.
 *
 * Privacy: unknown/invalid token → empty items (no enumeration oracle, no
 * other-order leak — mirrors OrderStatusResponse's not-found handling).
 *
 * Source: 2026-06-25-wandero-confirmation-affinity-recommendations.
 */
export const OrderRecommendationItemSchema = z.object({
  wallpaper_id: z.number().int().positive(),
  slug: z.string(),
  name: z.string(),
  og_image_url: z.string().optional(),
});
export type OrderRecommendationItem = z.infer<typeof OrderRecommendationItemSchema>;

export const OrderRecommendationsResponseSchema = z.object({
  items: z.array(OrderRecommendationItemSchema),
});
export type OrderRecommendationsResponse = z.infer<typeof OrderRecommendationsResponseSchema>;
